import classNames from 'classnames'
import React, { useState } from 'react'

type FilterButtonType = 'smartcard' | 'creditcard' | 'bankcard' | 'disabled'

export interface CardsFilterState {
	searchTerm: string
	typeFilters: string[]
	includeDisabled: boolean
}

type CardsFilterProps = {
	isCompany: boolean
	onFilterChange: (filters: CardsFilterState) => void
}

/**
 * CardsFilter Component - Search and filter cards by type
 * 
 * Features:
 * - Search by card name, type, or number (space-agnostic)
 * - Filter by card type (smartcard, tankkort/företagskort, bankkort)
 * - Filter for disabled cards
 * - Real-time filtering with active state indicators
 */
const CardsFilter: React.FC<CardsFilterProps> = ({ isCompany, onFilterChange }) => {
	const [searchTerm, setSearchTerm] = useState('')
	const [typeFilters, setTypeFilters] = useState<string[]>([])
	const [includeDisabled, setIncludeDisabled] = useState(false)

	// Trigger filter change whenever any filter changes
	const updateFilters = (
		search: string = searchTerm,
		types: string[] = typeFilters,
		disabled: boolean = includeDisabled
	) => {
		onFilterChange({
			searchTerm: search,
			typeFilters: types,
			includeDisabled: disabled
		})
	}

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setSearchTerm(value)
		updateFilters(value, typeFilters, includeDisabled)
	}

	const toggleTypeFilter = (type: string) => {
		const newTypes = typeFilters.includes(type)
			? typeFilters.filter((t) => t !== type)
			: [...typeFilters, type]
		setTypeFilters(newTypes)
		updateFilters(searchTerm, newTypes, includeDisabled)
	}

	const toggleDisabledFilter = () => {
		const newDisabled = !includeDisabled
		setIncludeDisabled(newDisabled)
		updateFilters(searchTerm, typeFilters, newDisabled)
	}

	const isTypeFilterActive = (type: string) => typeFilters.includes(type)

	return (
		<div className="cards-filter">
			{/* Search Input */}
			<div className="cards-filter__search">
				<input
					type="text"
					placeholder="Sök efter kort..."
					name="Sökfält för kortfilter"
					value={searchTerm}
					onChange={handleSearchChange}
					className="cards-filter__search-input"
				/>
			</div>

			{/* Type Filter Buttons */}
			<div className="cards-filter__buttons" aria-description="Filterera kort efter typ">
				<button
					className={classNames('cards-filter__button', {
						'cards-filter__button--active': isTypeFilterActive('P')
					})}
					onClick={() => toggleTypeFilter('P')}
					type="button"
				>
					Smartkort
				</button>

				<button
					className={classNames('cards-filter__button', {
						'cards-filter__button--active': isTypeFilterActive('K')
					})}
					onClick={() => toggleTypeFilter('K')}
					type="button"
				>
					{isCompany ? 'Företagskort' : 'Tankkort'}
				</button>

				<button
					className={classNames('cards-filter__button', {
						'cards-filter__button--active': isTypeFilterActive('B')
					})}
					onClick={() => toggleTypeFilter('B')}
					type="button"
				>
					Bankkort
				</button>

				<button
					className={classNames('cards-filter__button', {
						'cards-filter__button--active': includeDisabled
					})}
					onClick={toggleDisabledFilter}
					type="button"
				>
					Spärrat
				</button>
			</div>
		</div>
	)
}

export default CardsFilter
